import React, { useRef } from 'react';

import {
  SlDropdown,
  SlMenu,
  SlMenuItem,
} from '@shoelace-style/shoelace/dist/react';
import { TeamRole } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import { useSetAtom } from 'jotai';

import { IconButton } from '~shared/components/buttons';
import { FocusIndicator } from '~shared/components/FocusIndicator';
import { IconEllipsis } from '~shared/components/icons/standard';
import { useSelectedTeamRoleQuery } from '~teacher/components/Account/hooks';
import { isConfirmLeaveTeamDialogOpenAtom } from '~teacher/components/Account/TeamMembers/ConfirmLeaveTeamDialog';
import { isRemoveFromTeamMemberDialogOpenAtom } from '~teacher/components/Account/TeamMembers/RemoveFromTeamDialog';
import { selectedPublicIdsAtom } from '~teacher/components/Account/TeamMembers/TeamMembersTable/atoms';
import { TransferTeamOwnershipDialog } from '~teacher/components/Account/TeamMembers/TransferTeamOwnershipDialog';
import { useAccount } from '~teacher/hook/account';

function CurrentUser() {
  const roleQuery = useSelectedTeamRoleQuery();
  const dropdownRef = React.useRef<any>(null);
  const setIsRemoveFromTeamDialogOpen = useSetAtom(
    isConfirmLeaveTeamDialogOpenAtom
  );

  if (roleQuery.data === TeamRole.Owner) {
    return <div className="w-12" />;
  }

  return (
    <SlDropdown ref={dropdownRef}>
      <IconButton
        onClick={() => {
          dropdownRef.current.show();
        }}
        slot="trigger"
        className="group relative actions hocus:bg-cyan-50 flex items-center justify-center w-12 h-12 p-0 m-0 border-0 rounded-2 appearance-none outline-none transition ease-in-out duration-150 cursor-pointer text-cyan-600 hover:text-cyan-700 focus:text-cyan-700 hover:bg-cyan-50 focus:bg-cyan-50 bg-transparent"
      >
        <IconEllipsis />
        <FocusIndicator />
      </IconButton>
      <SlMenu className="action-menu">
        <EllipsisMenuItem onClick={() => setIsRemoveFromTeamDialogOpen(true)}>
          {translate('Leave Team')}
        </EllipsisMenuItem>
      </SlMenu>
    </SlDropdown>
  );
}

interface OtherUserProps {
  publicId: string;
}

function OtherUser({ publicId }: OtherUserProps) {
  const setIsRemoveFromTeamDialogOpen = useSetAtom(
    isRemoveFromTeamMemberDialogOpenAtom
  );
  const setSelectedPublicIds = useSetAtom(selectedPublicIdsAtom.replaceAtom);
  const dropdownRef = useRef<any>(null);
  const openTransferOwnershipDialog =
    TransferTeamOwnershipDialog.useOpenDialog();

  const roleQuery = useSelectedTeamRoleQuery();

  if (roleQuery.data === TeamRole.Member) {
    return <div className="w-12" />;
  }

  return (
    <SlDropdown ref={dropdownRef}>
      <IconButton
        onClick={() => {
          dropdownRef.current.show();
        }}
        slot="trigger"
        className="group relative actions hocus:bg-cyan-50 flex items-center justify-center w-12 h-12 p-0 m-0 border-0 rounded-2 appearance-none outline-none transition ease-in-out duration-150 cursor-pointer text-cyan-600 hover:text-cyan-700 focus:text-cyan-700 hover:bg-cyan-50 focus:bg-cyan-50 bg-transparent cursor-pointer"
      >
        <IconEllipsis />
        <FocusIndicator />
      </IconButton>
      <SlMenu className="action-menu">
        <EllipsisMenuItem
          onClick={() => {
            setSelectedPublicIds(new Set([publicId]));
            setIsRemoveFromTeamDialogOpen(true);
          }}
        >
          {translate('Remove Member')}
        </EllipsisMenuItem>
        {roleQuery.data === TeamRole.Owner ? (
          <EllipsisMenuItem
            onClick={() => {
              setSelectedPublicIds(new Set([publicId]));
              openTransferOwnershipDialog();
            }}
          >
            {translate('Transfer Ownership')}
          </EllipsisMenuItem>
        ) : null}
      </SlMenu>
    </SlDropdown>
  );
}

interface EllipsisMenuProps {
  publicId: string;
  role: TeamRole;
}

export function EllipsisMenu({ publicId, role }: EllipsisMenuProps) {
  const isCurrentUserQuery = useAccount({
    select: (account) => account.publicId === publicId,
    enabled: role !== TeamRole.Owner,
  });

  if (role === TeamRole.Owner) {
    return <div className="w-12" />;
  }

  if (isCurrentUserQuery.data) {
    return <CurrentUser />;
  } else {
    return <OtherUser publicId={publicId} />;
  }
}

type EllipsisMenuItemProps = React.ComponentProps<typeof SlMenuItem>;

export function EllipsisMenuItem({
  children,
  ...props
}: EllipsisMenuItemProps) {
  return (
    <SlMenuItem className="action-menu" {...props}>
      {children}
    </SlMenuItem>
  );
}
